/**
 * Pinia portal nav-summary store (Client Portal v2, D6-polish #3/#4).
 *
 * Owns the sidebar at-a-glance figures fed by GET /api/portal/nav-summary
 * (the ``subs`` map under each nav label). Previously PortalLayout fetched
 * these into local component data; moving the fetch here lets a routed child
 * page trigger a LIVE refresh.
 *
 * Why a store: the Approvals page (a routed child of PortalLayout) must be
 * able to refresh the sidebar sub-text right after an approve /
 * request-changes action so "N awaiting . M in changes" updates without a
 * page reload (#3). A shared store is the clean seam -- a portal mutation
 * emits ``sidebar:refresh`` on action success and this store refetches (see
 * the delegation note below); PortalLayout renders ``summary`` reactively.
 *
 * The payload is client-scoped server-side (NULL client -> 403); a failed
 * fetch is non-fatal (the sidebar renders labels without sub-lines).
 *
 * Generic sidebar-refresh delegation (sidebar-emit chain PR-E, 2026-07-15,
 * https://trello.com/c/g9K52FsA): rather than have the child pages hand-wire
 * ``navSummary.refresh()`` calls, this store now SUBSCRIBES to the single
 * ``sidebar:refresh`` event (see stores/sidebarRefresh.js) the same way the
 * distributor summary store does. A portal mutation emits
 * ``emitSidebarRefresh('<portal-row-id>')`` on success; ``subscribe()`` refetches
 * when that id is a row THIS layout owns (``PORTAL_KEYS``). One mechanism across
 * shells; no per-page refresh() plumbing.
 *
 * Portal is CLIENT-scoped server-side (not distributor/tenant-scoped), so --
 * unlike distributorSummary -- there is no impersonation epoch, cap gate, or
 * stale-tenant guard here. The relevance filter is just "is this id a portal
 * row?", then the existing non-fatal ``_fetch()``.
 */
import { defineStore } from "pinia";
import { ref } from "vue";
import axios from "@/axios";
import { useEventBus } from "@/stores/eventBus";
import { SIDEBAR_REFRESH_EVENT } from "@/stores/sidebarRefresh";
import { describeError } from "@/utils/safeErrorLog";

// The row ids the Portal sidebar owns -- the ``id`` values on a
// ``sidebar:refresh`` event that are RELEVANT to this layout. These match the
// ``item.id`` values PortalLayout renders (Overview / Orders / Approvals /
// Budget / Allowance / Team). The nav-summary is ONE endpoint returning every
// counter, so ANY owned id changing means a single coarse refetch -- the id is
// only a relevance token, not a per-key selector.
//
// ``portal.scope.changed`` is the header store-scope switcher (D3 gap closure,
// 2026-07-15): changing the scope pill re-scopes the portal surfaces and so the
// nav-summary should refetch too. It routes through this same subscribe path
// rather than a bespoke watcher-to-refresh, keeping ONE mechanism.
const PORTAL_KEYS = Object.freeze(
    new Set([
        "overview",
        "orders",
        "approvals",
        "budget",
        "allowance",
        "team",
        "portal.scope.changed",
    ]),
);

export const usePortalNavSummaryStore = defineStore("portalNavSummary", () => {
    // The raw nav-summary payload, or null until the first successful load.
    const summary = ref(null);
    const loading = ref(false);

    async function _fetch() {
        loading.value = true;
        try {
            const resp = await axios.get("/api/portal/nav-summary");
            summary.value = resp?.data ?? null;
        } catch (e) {
            // Non-fatal: a 403 (non-client caller) or transient error just
            // leaves the sidebar without its sub-lines. Keep any prior summary
            // rather than blanking a good one on a transient refresh failure.
            //
            // 403 is intentional (non-client caller -- NULL client_id). The
            // portalScope.load() onForbidden callback handles the redirect;
            // logging a red console error here is misleading noise. Only log
            // genuinely unexpected errors.
            if (e?.response?.status !== 403) {
                // eslint-disable-next-line no-console
                console.error(describeError(e));
            }
        } finally {
            loading.value = false;
        }
    }

    // First load (on portal entry).
    async function load() {
        await _fetch();
    }

    function reset() {
        summary.value = null;
        loading.value = false;
    }

    // Event-driven refresh entry point (generic sidebar:refresh delegation).
    //
    // Delegates to the existing non-fatal _fetch() -- it deliberately inherits
    // the store's non-fatal-on-403 behavior (a transient refresh failure keeps
    // any prior good summary rather than blanking it). Portal has no tenant /
    // impersonation / cap guard, so the only relevance decision is made at the
    // subscribe seam (PORTAL_KEYS.has(id)); by the time we are here the id is
    // already known-relevant.
    async function maybeRefetch() {
        await _fetch();
    }

    // Register the portal summary's refresh handler on the event bus and return
    // the teardown fn. Called once from PortalLayout's mount (scoped users
    // only); torn down on unmount so no listener survives a shell switch.
    //
    // Delegation: subscribes to the SINGLE ``sidebar:refresh`` event and only
    // refetches when the event's ``id`` is a row THIS layout owns
    // (``PORTAL_KEYS``). The id is the relevance token -- there is no per-domain
    // event to enumerate.
    function subscribe() {
        const bus = useEventBus();
        return bus.on(SIDEBAR_REFRESH_EVENT, (payload) => {
            if (!PORTAL_KEYS.has(payload?.id)) return;
            maybeRefetch();
        });
    }

    return { summary, loading, load, reset, maybeRefetch, subscribe };
});
